import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  doc,
  updateDoc
} from 'firebase/firestore';
import { LogEntry, UserProfile } from '../types';

// Helper to check standard React/Vite env var patterns safely
const getEnv = (key: string) => {
  // 1. Try standard process.env (React Scripts, Next.js)
  if (typeof process !== 'undefined' && process.env) {
    if (process.env[key]) return process.env[key];
    if (process.env[`REACT_APP_${key}`]) return process.env[`REACT_APP_${key}`];
    if (process.env[`VITE_${key}`]) return process.env[`VITE_${key}`];
  }
  
  // 2. Try import.meta.env (Vite native)
  try {
     // @ts-ignore
     if (typeof import.meta !== 'undefined' && import.meta.env) {
        // @ts-ignore
        if (import.meta.env[key]) return import.meta.env[key];
        // @ts-ignore
        if (import.meta.env[`VITE_${key}`]) return import.meta.env[`VITE_${key}`];
     }
  } catch(e) {}
  
  return undefined;
};

// Placeholder config - relies on env variables
const firebaseConfig = {
  apiKey: getEnv('FIREBASE_API_KEY'),
  authDomain: getEnv('FIREBASE_AUTH_DOMAIN'),
  projectId: getEnv('FIREBASE_PROJECT_ID'),
  storageBucket: getEnv('FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: getEnv('FIREBASE_MESSAGING_SENDER_ID'),
  appId: getEnv('FIREBASE_APP_ID'),
};

// Check if config is valid to decide mode
// We export this so the UI can warn the user if they are in local demo mode
export const isUsingFirebase = !!(firebaseConfig.apiKey && firebaseConfig.projectId);

let auth: any;
let db: any;

if (isUsingFirebase) {
  try {
    const app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    console.log("Mindul Flow: Connected to Firebase Cloud.");
  } catch (e) {
    console.error("Mindul Flow: Firebase initialization failed", e);
  }
} else {
    console.warn("Mindul Flow: Firebase keys missing. Running in Local Demo Mode. Data will not sync across devices.");
}

// Helper to generate consistent Mock IDs based on email
const generateMockUid = (email: string) => {
    // Simple hash to create a consistent ID from email for demo purposes
    return `demo-user-${email.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`;
};

// --- Auth Services ---

export const authService = {
  onAuthStateChanged: (callback: (user: UserProfile | null) => void) => {
    if (isUsingFirebase && auth) {
      return onAuthStateChanged(auth, (user: User | null) => {
        callback(user ? { uid: user.uid, email: user.email } : null);
      });
    } else {
      // Demo Mode Persistence
      const stored = localStorage.getItem('demo_user');
      if (stored) callback(JSON.parse(stored));
      else callback(null);
      return () => {};
    }
  },
  signIn: async (email: string, pass: string) => {
    if (isUsingFirebase && auth) return signInWithEmailAndPassword(auth, email, pass);
    // Demo
    const uid = generateMockUid(email);
    const user = { uid, email };
    localStorage.setItem('demo_user', JSON.stringify(user));
    window.location.reload(); 
    return { user };
  },
  signUp: async (email: string, pass: string) => {
    if (isUsingFirebase && auth) return createUserWithEmailAndPassword(auth, email, pass);
    // Demo
    const uid = generateMockUid(email);
    const user = { uid, email };
    localStorage.setItem('demo_user', JSON.stringify(user));
    window.location.reload();
    return { user };
  },
  logout: async () => {
    if (isUsingFirebase && auth) return signOut(auth);
    localStorage.removeItem('demo_user');
    window.location.reload();
  }
};

// --- Firestore/Data Services ---

export const dataService = {
  addLog: async (entry: Omit<LogEntry, 'id'>) => {
    if (isUsingFirebase && db) {
      return addDoc(collection(db, 'logs'), entry);
    }
    // Demo LocalStorage
    const logs = JSON.parse(localStorage.getItem('demo_logs') || '[]');
    const newLog = { ...entry, id: Date.now().toString() };
    logs.push(newLog);
    localStorage.setItem('demo_logs', JSON.stringify(logs));
    // Trigger generic event for demo updates
    window.dispatchEvent(new Event('storage-update'));
    return newLog;
  },

  updateLog: async (id: string, updates: Partial<LogEntry>) => {
    if (isUsingFirebase && db) {
      const docRef = doc(db, 'logs', id);
      return updateDoc(docRef, updates);
    }
    const logs = JSON.parse(localStorage.getItem('demo_logs') || '[]');
    const index = logs.findIndex((l: LogEntry) => l.id === id);
    if (index !== -1) {
      logs[index] = { ...logs[index], ...updates };
      localStorage.setItem('demo_logs', JSON.stringify(logs));
      window.dispatchEvent(new Event('storage-update'));
    }
  },

  subscribeToLogs: (userId: string, callback: (logs: LogEntry[]) => void) => {
    if (isUsingFirebase && db) {
      const q = query(
        collection(db, 'logs'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );
      return onSnapshot(q, (snapshot) => {
        const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as LogEntry));
        callback(logs);
      });
    }
    
    // Demo Listener
    const load = () => {
      const logs = JSON.parse(localStorage.getItem('demo_logs') || '[]');
      const userLogs = logs
        .filter((l: LogEntry) => l.userId === userId)
        .sort((a: LogEntry, b: LogEntry) => b.timestamp - a.timestamp);
      callback(userLogs);
    };
    load();
    window.addEventListener('storage-update', load);
    return () => window.removeEventListener('storage-update', load);
  }
};